/**
 * Resource Plan M1 Configuration hooks — the Labor Category Catalog (Epic 11), the Rate
 * Card (Epic 12), and the Planning Constants (Epic 14).
 *
 * The response types come straight from `@/schemas/rate-cards`, which is also what the API
 * modules return — so the cost field being OPTIONAL in the type is visible to the UI. That
 * is deliberate: `avgInternalCost` is genuinely absent from an [ACCT] payload
 * (non-negotiable 3), so the component has to handle its absence rather than render a
 * blank cell over a value that was sent anyway.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { invalidateTierCaches } from "@/hooks/use-engagement-standard-tiers";
import { laborCategoryKeys, planningConstantKeys, rateCardKeys } from "@/lib/query";
import type { LaborCategoryView, RateBasisView, RateCardView } from "@/schemas/rate-cards";

export type { LaborCategoryView, RateBasisView, RateCardView };

export interface PlanningConstant {
  name: string;
  key: string;
  label: string;
  description: string;
  value: string;
  isDefault: boolean;
}

async function readJsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

// ── Rate Card ─────────────────────────────────────────────────────────────────

export function useRateCard() {
  return useQuery({
    queryKey: rateCardKeys.current(),
    queryFn: async (): Promise<RateCardView> =>
      readJsonOrThrow<RateCardView>(await fetch("/api/rate-cards/current")),
  });
}

/** The category rows and bill rates a card save sends back. */
export interface RateCardSavePayload {
  categories: Array<{ id: string; projectMixPercent: string; avgInternalCost: string }>;
  rates: Array<{ laborCategoryId: string; rateBasisId: string; billRate: string | null }>;
  effectiveFrom: string;
}

export function useSaveRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RateCardSavePayload) =>
      readJsonOrThrow<{ ok: true; ratesOpened: number; ratesClosed: number }>(
        await fetch("/api/rate-cards/current", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        }),
      ),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: rateCardKeys.all });
      // The Catalog shows a rate count per category, which a card save moves.
      qc.invalidateQueries({ queryKey: laborCategoryKeys.all });
      toast.success(
        result.ratesOpened === 0 && result.ratesClosed === 0
          ? "Rate Card saved"
          : `Rate Card saved — ${result.ratesOpened} rate${result.ratesOpened === 1 ? "" : "s"} opened, ${result.ratesClosed} closed`,
      );
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to save the Rate Card"),
  });
}

export function useUpdateRateBasis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      marginFloorPercent?: string | null;
      marginAmberBandPoints?: string | null;
      active?: boolean;
    }) => {
      const { id, ...patch } = input;
      return readJsonOrThrow<RateBasisView>(
        await fetch(`/api/rate-basis/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rateCardKeys.all });
      toast.success("Margin policy updated");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to update the Rate Basis"),
  });
}

export function useSetTierRateBasis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { tierId: string; rateBasisId: string; reason?: string }) => {
      const { tierId, ...body } = input;
      return readJsonOrThrow<{ tierName: string; rateBasisCode: string }>(
        await fetch(`/api/engagement-standard-tiers/${tierId}/rate-basis`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
      );
    },
    onSuccess: (result) => {
      // Not just the card: the tier list on Settings ▸ Tiers and the Accounts tier picker
      // both render this mapping, and invalidating only rateCardKeys leaves them stale.
      invalidateTierCaches(qc);
      toast.success(`${result.tierName} now prices on ${result.rateBasisCode}`);
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to re-map the tier"),
  });
}

// ── Labor Category Catalog ────────────────────────────────────────────────────

export function useLaborCategories() {
  return useQuery({
    queryKey: laborCategoryKeys.list(),
    queryFn: async (): Promise<LaborCategoryView[]> => {
      const data = await readJsonOrThrow<{ categories: LaborCategoryView[] }>(
        await fetch("/api/labor-categories"),
      );
      return data.categories;
    },
  });
}

export interface LaborCategoryInput {
  name: string;
  description?: string | null;
  disciplineId?: string | null;
  isDefaultForDiscipline?: boolean;
  expectedRatingMin?: number | null;
  expectedRatingMax?: number | null;
  active?: boolean;
  sortOrder?: number;
}

export function useCreateLaborCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LaborCategoryInput) =>
      readJsonOrThrow<LaborCategoryView>(
        await fetch("/api/labor-categories", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: laborCategoryKeys.all });
      // A new category joins the card at 0.00% mix, so the card's running total changes.
      qc.invalidateQueries({ queryKey: rateCardKeys.all });
      toast.success("Labor Category created");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to create the Labor Category"),
  });
}

export function useUpdateLaborCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<LaborCategoryInput> & { id: string }) => {
      const { id, ...patch } = input;
      return readJsonOrThrow<LaborCategoryView>(
        await fetch(`/api/labor-categories/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: laborCategoryKeys.all });
      qc.invalidateQueries({ queryKey: rateCardKeys.all });
      toast.success("Labor Category updated");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to update the Labor Category"),
  });
}

// ── Planning Constants ────────────────────────────────────────────────────────

export function usePlanningConstants() {
  return useQuery({
    queryKey: planningConstantKeys.list(),
    queryFn: async (): Promise<PlanningConstant[]> => {
      const data = await readJsonOrThrow<{ constants: PlanningConstant[] }>(
        await fetch("/api/planning-constants"),
      );
      return data.constants;
    },
  });
}

export function useSetPlanningConstant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; value: string }) =>
      readJsonOrThrow<PlanningConstant>(
        await fetch(`/api/planning-constants/${input.name}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ value: input.value }),
        }),
      ),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: planningConstantKeys.all });
      // The margin floor and amber band feed the Rate Card's floor badges.
      qc.invalidateQueries({ queryKey: rateCardKeys.all });
      toast.success(`${result.label} updated`);
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to update the constant"),
  });
}
